<script lang="ts">
  import 'simplebar';
  import 'simplebar/dist/simplebar.css';
  import { fade } from 'svelte/transition';
  import { debounce } from 'lodash-es';
  import { createEventDispatcher, onMount } from 'svelte';

  import Media from './Media.svelte';
  import MediaPreviewChevronLeft from '$icons/MediaPreviewChevronLeftIcon.svelte';
  import MediaPreviewChevronRight from '$icons/MediaPreviewChevronRightIcon.svelte';

  import type { WithCreatorData } from 'garden-common';
  import type { FPost } from 'garden-common';
  import { updateBlur } from 'garden-utils/index';
  import SensitiveContent from '../../common/media/SensitiveContent/index.svelte';

  export let post: Partial<FPost<string>> & Partial<WithCreatorData>;

  let disableMediaIndexUpdates = false;
  let cursorHovering = false;

  let displayedMediaIndex = 0;

  let disableMediaIndexUpdatesTimeout: ReturnType<typeof setTimeout>;

  let imageDataUrl: string | undefined = undefined;
  let scrollContainer: HTMLElement;
  let blurContainer: HTMLElement;

  const debouncedUpdateScrollFromMediaIndex = debounce(updateScrollPosFromMediaIndex, 500);

  $: totalMediaCount = post?.media?.length;

  const dispatch = createEventDispatcher();

  function onClickNext() {
    if (displayedMediaIndex < totalMediaCount - 1) {
      displayedMediaIndex++;
    }
    // Always scroll to make sure the image does not stay somewhere in
    // the middle bause of wheel scroll. (snapping does not work with wheel scroll)
    updateScrollPosFromMediaIndex();
  }

  function onClickPrev() {
    if (displayedMediaIndex > 0) {
      displayedMediaIndex--;
    }
    updateScrollPosFromMediaIndex();
  }

  function updateScrollPosFromMediaIndex() {
    // Disabling media index updates for a second here to prevent
    // media index flashing caused by automatic media index updates.
    if (disableMediaIndexUpdatesTimeout) {
      clearTimeout(disableMediaIndexUpdatesTimeout);
    }
    disableMediaIndexUpdates = true;
    scrollContainer?.scrollTo({
      left: displayedMediaIndex * scrollContainer.clientWidth,
      behavior: 'smooth'
    });
    disableMediaIndexUpdatesTimeout = setTimeout(() => {
      disableMediaIndexUpdates = false;
    }, 1000);

    updateImageDataUrl();
  }

  function onPointerLeave() {
    setTimeout(() => {
      cursorHovering = false;
    }, 200);
  }

  function onScroll() {
    if (!scrollContainer) {
      return;
    }

    if (!disableMediaIndexUpdates) {
      displayedMediaIndex = Math.round(
        scrollContainer.scrollLeft / (scrollContainer.clientWidth - 1)
      );
    }
    debouncedUpdateScrollFromMediaIndex();
  }

  const updateImageDataUrl = () => {
    if (!blurContainer || !post?.media[displayedMediaIndex]?.blurhash) {
      return;
    }
    const hash = post?.media[displayedMediaIndex]?.blurhash[0];
    if (!hash) {
      return;
    }

    imageDataUrl = updateBlur(hash, {
      width: blurContainer.offsetWidth,
      height: blurContainer.offsetHeight
    });
  };

  function handleMediaClick() {
    dispatch('clickedMedia');
  }

  onMount(() => {
    updateImageDataUrl();
  });
</script>

<!-- Z-10 to fix z-fighting with navtab popup -->
<div
  class="h-full w-full relative z-[10] isolate"
  on:pointerenter={() => (cursorHovering = true)}
  on:pointerleave={onPointerLeave}
  bind:this={blurContainer}
>
  {#if imageDataUrl}
    {#key imageDataUrl}
      <div class="w-full h-full absolute ">
        <Media class="w-full h-full" src={imageDataUrl} />
      </div>
    {/key}
  {/if}

  <div class="w-full absolute h-full ">
    <button
      class="flex w-full h-full items-center"
      on:scroll={onScroll}
      on:click={() => {
        handleMediaClick();
      }}
      bind:this={scrollContainer}
    >
      {#if post.media}
        {#if !post.isSensitive}
          {#key post?.media[displayedMediaIndex]?.images[0]}
            {#if post.media.length > 1}
              <Media
                src={post.media[displayedMediaIndex]?.thumbnails[0]}
                class="h-full w-full bg-transparent pointer-events-none object-contain flex-shrink-0 snap-center snap-mandatory"
              />
            {:else}
              <Media
                src={post.media[0]?.thumbnails[0]}
                class="h-full w-full bg-transparent pointer-events-none object-contain flex-shrink-0 snap-center snap-mandatory"
              />
            {/if}
          {/key}
        {:else}
          <SensitiveContent src={post.media[0].thumbnails[0]} isSensitive={post.isSensitive} />
        {/if}
      {/if}
    </button>

    <!-- Controls overlay -->
    <div class="h-full absolute inset-0 pointer-events-none">
      <div class="relative h-full bg-transparent">
        <!-- Chevron left -->
        {#if post?.media?.length > 1}
          <button
            class="left-0 absolute inset-y-0 active:enabled:scale-95 duration-200 pointer-events-auto disabled:opacity-40"
            disabled={displayedMediaIndex < 1}
            on:click={onClickPrev}
          >
            <MediaPreviewChevronLeft />
          </button>
        {/if}
        {#if cursorHovering && post?.media?.length > 1}
          <div
            class="w-full bottom-4 grid place-items-center absolute"
            in:fade={{ duration: 60 }}
            out:fade={{ duration: 150 }}
          >
            <div
              class="bg-[#111111] bg-opacity-75 text-white px-3 py-1 text-xs rounded-md border-grey-neutral-30 border border-opacity-25 backdrop-blur-lg font-switzer"
            >
              {displayedMediaIndex + 1}/{totalMediaCount}
            </div>
          </div>
        {/if}

        {#if post?.media?.length > 1}
          <!-- Chevron right -->
          <button
            class="right-0 absolute inset-y-0 active:enabled:scale-95 duration-200 pointer-events-auto disabled:opacity-40"
            disabled={displayedMediaIndex === totalMediaCount - 1}
            on:click={onClickNext}
          >
            <MediaPreviewChevronRight />
          </button>
        {/if}
      </div>
    </div>
  </div>
</div>
